import { useState, useEffect } from 'react'
import {
  Stack,
  Table,
  Text,
  Badge,
  Button,
  Group,
  Box,
  Skeleton,
  Alert,
} from '@mantine/core'
import { IconAlertCircle, IconBulb, IconDatabaseOff } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { databaseApi, type DbRecommendation } from '@/api/database'
import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { RecommendationDetail } from './RecommendationDetail'

const STATUS_RDS: Record<string, string> = {
  available: 'disponível',
  stopped: 'parada',
  starting: 'iniciando',
  stopping: 'parando',
  modifying: 'em alteração',
  backing_up: 'em backup',
  rebooting: 'reiniciando',
  failed: 'falhou',
}

/** Rotulo em pt-BR; valor cru da AWS quando nao conhecido, para nao esconder estado novo. */
function statusRds(status: string): string {
  return STATUS_RDS[status] ?? status
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
}

const STATUS_COLOR: Record<string, string> = {
  open: 'blue',
  applied: 'green',
  dismissed: 'gray',
}

export function RecommendationsTab() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id

  const [selectedDb, setSelectedDb] = useState<string | null>(null)
  const [selectedRec, setSelectedRec] = useState<DbRecommendation | null>(null)

  const instancesQuery = useQuery({
    queryKey: ['rds-instances', tenantId],
    queryFn: () => databaseApi.listInstances(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  })

  const recsQuery = useQuery({
    queryKey: ['rds-recommendations', tenantId, selectedDb],
    queryFn: () => databaseApi.listRecommendations(tenantId!, selectedDb!),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (instancesQuery.data?.items?.length && !selectedDb) {
      setSelectedDb(instancesQuery.data.items[0].db_identifier)
    }
  }, [instancesQuery.data, selectedDb])

  const instances = instancesQuery.data?.items ?? []
  const recs = recsQuery.data?.items ?? []

  if (!tenantId) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        Selecione um tenant para visualizar recomendações
      </Text>
    )
  }

  return (
    <>
      <Stack gap="md">
        {/* Instance selector */}
        <WidgetCard accent="primary" p={0}>
          {/* Com uma instancia so, a linha compacta nao e selecionavel — mandar selecionar
              seria dar uma ordem que o leitor nao tem como cumprir. */}
          <CardHeader title={instances.length === 1 ? 'Instância' : 'Selecione a instância'} />
          <Box p="md">
            {instancesQuery.isLoading ? (
              <Stack gap="xs">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={36} />
                ))}
              </Stack>
            ) : instances.length === 0 ? (
              <EmptyState
                bare
                size="sm"
                accent="primary"
                icon={IconDatabaseOff}
                title="Nenhuma instância RDS encontrada"
                description="Configure o acesso AWS do tenant para listar instâncias monitoráveis."
              />
            ) : instances.length === 1 ? (
              /* Uma instancia so: tres colunas com cabecalho para escolher entre uma opcao
                 e maquinaria sem decisao por tras. Linha compacta diz o mesmo. */
              <Group gap="sm" wrap="nowrap">
                <Text size="sm" fw={600}>{instances[0].db_identifier}</Text>
                <Text size="sm" c="dimmed">{instances[0].engine}</Text>
                <Badge size="sm" color={instances[0].status === 'available' ? 'green' : 'red'}>
                  {statusRds(instances[0].status)}
                </Badge>
              </Group>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Identificador</Table.Th>
                    <Table.Th>Motor</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {instances.map((inst) => (
                    <Table.Tr
                      key={inst.db_identifier}
                      onClick={() => setSelectedDb(inst.db_identifier)}
                      style={{
                        cursor: 'pointer',
                        // --ai-bg-subtle e o realce reativo dos dois temas (index.css).
                        // Antes era var(--mantine-color-dark-6): superficie quase preta no
                        // tema claro, que virava a mancha mais pesada da tela inteira.
                        background:
                          selectedDb === inst.db_identifier
                            ? 'var(--ai-bg-subtle)'
                            : undefined,
                      }}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>{inst.db_identifier}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{inst.engine}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={inst.status === 'available' ? 'green' : 'red'}>
                          {statusRds(inst.status)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Box>
        </WidgetCard>

        {/* Recommendations list */}
        {selectedDb && (
          <WidgetCard accent="none" p={0}>
            <CardHeader
              title="Recomendações"
              badge={
                recs.length > 0
                  ? { value: recs.length, color: 'blue' }
                  : undefined
              }
            />
            <Box p="md">
              {recsQuery.isLoading ? (
                <Stack gap="xs">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={36} />
                  ))}
                </Stack>
              ) : recsQuery.isError ? (
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Erro">
                  Falha ao carregar recomendações
                </Alert>
              ) : recs.length === 0 ? (
                <EmptyState
                  bare
                  size="sm"
                  accent="success"
                  icon={IconBulb}
                  title="Sem recomendações no momento"
                  description="O motor de análise não identificou recomendações pendentes para esta instância."
                />
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Tipo</Table.Th>
                      <Table.Th>Título</Table.Th>
                      <Table.Th>Severidade</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Impacto</Table.Th>
                      <Table.Th>Ações</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {recs.map((rec) => (
                      <Table.Tr key={rec.id}>
                        <Table.Td>
                          <Text size="sm">{rec.type}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="sm"
                            fw={500}
                            style={{ cursor: 'pointer' }}
                            c="blue"
                            onClick={() => setSelectedRec(rec)}
                          >
                            {rec.title}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={SEVERITY_COLOR[rec.severity] ?? 'gray'}>
                            {rec.severity}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={STATUS_COLOR[rec.status] ?? 'gray'}>
                            {rec.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{rec.estimated_impact ?? '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          {rec.status === 'open' && (
                            <Group gap="xs">
                              <Button size="compact-xs" onClick={() => setSelectedRec(rec)}>
                                Aplicar
                              </Button>
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                onClick={() => setSelectedRec(rec)}
                              >
                                Dispensar
                              </Button>
                            </Group>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Box>
          </WidgetCard>
        )}
      </Stack>

      {tenantId && (
        <RecommendationDetail
          rec={selectedRec}
          tenantId={tenantId}
          onClose={() => setSelectedRec(null)}
        />
      )}
    </>
  )
}
